import { useEffect, useRef, useState } from 'react';
import { useBookSavedViews } from '../../../hooks/useBookSavedViews';
import type { BookFilterState, BookSavedView } from '../../../types/manageBooks';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  currentFilters: BookFilterState;
  onLoad: (filters: BookFilterState) => void;
}

export function BookSavedViews({ currentFilters, onLoad }: Props) {
  const { t } = useTranslation();
  const { views, save, remove, max } = useBookSavedViews();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleSave = () => {
    const name = window.prompt(t('App.Admin.Books.BookSavedViews.PromptName'));
    if (!name) return;
    const saved = save(name.trim(), currentFilters);
    if (!saved) {
      window.alert(t('App.Admin.Books.BookSavedViews.HitLimitAlert', { max }));
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="border rounded px-3 py-2 text-sm min-h-[44px]"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {t('App.Admin.Books.BookSavedViews.ButtonLabel', { count: views.length })}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 bg-white shadow-lg border rounded z-20 p-2"
          role="menu"
          aria-label={t('App.Admin.Books.BookSavedViews.AriaLabel')}
        >
          <button
            type="button"
            onClick={() => { handleSave(); setOpen(false); }}
            className="w-full text-left px-2 py-1 text-sm text-primary-600 hover:bg-neutral-100"
            role="menuitem"
          >
            {t('App.Admin.Books.BookSavedViews.SaveCurrentLabel')}
          </button>
          <hr className="my-1" />
          {views.length === 0 && (
            <p className="text-sm text-neutral-500 px-2 py-1">{t('App.Admin.Books.BookSavedViews.NoViews')}</p>
          )}
          {views.map((v: BookSavedView) => (
            <div
              key={v.id}
              className="flex items-center justify-between px-2 py-1 hover:bg-neutral-100"
            >
              <button
                type="button"
                onClick={() => { onLoad(v.filters); setOpen(false); }}
                className="text-sm text-left flex-1 truncate"
                role="menuitem"
              >
                {v.name}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t('App.Admin.Books.BookSavedViews.DeleteConfirm', { name: v.name }))) remove(v.id);
                }}
                className="text-xs text-red-600 ml-2"
                aria-label={t('App.Admin.Books.BookSavedViews.DeleteAriaLabel', { name: v.name })}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
